import { useEffect, useRef, useState } from "react";

import { useOverlayDismiss } from "../../lib/useOverlayDismiss";
import { ApiError, apiClient } from "../../api/client";
import type { MeResponse, Position } from "../../api/types";
import { hasPermission } from "../../auth/permissions";
import { useIdempotencyKey } from "../../lib/idempotencyKey";
import { PositionDetail } from "./PositionDetail";
import { PositionFormModal, employmentTypeLabel } from "./PositionFormModal";
import { RespondModal } from "./RespondModal";
import { VideoEmbed } from "./VideoEmbed";

type LoadStatus = "loading" | "ready" | "error";
type StatusFilter = "open" | "closed" | "all";

// PositionsListPage is the Open Positions board: a card grid of open roles
// with inline video previews and a Respond button. Managers additionally get
// a status filter (open/closed/all), a "New position" button, and per-card
// edit / close / delete controls. Position detail opens as a modal — there is
// no /hiring/{id} route.
export function PositionsListPage({ me }: { me: MeResponse }) {
  const canManage = hasPermission(me, "positions:manage");
  const [positions, setPositions] = useState<Position[]>([]);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("open");
  const [formOpen, setFormOpen] = useState(false);
  const [formPosition, setFormPosition] = useState<Position | null>(null);
  const [detail, setDetail] = useState<Position | null>(null);
  const [responding, setResponding] = useState<Position | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Position | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const statusKey = useIdempotencyKey();
  const deleteKey = useIdempotencyKey();
  // Bumped on every list (re)load so an in-flight "load more" started under a
  // previous filter can detect it is stale and discard its page.
  const listGen = useRef(0);
  const overlayDismiss = useOverlayDismiss(() => setPendingDelete(null), deleting);

  useEffect(() => {
    let cancelled = false;
    listGen.current += 1;
    setStatus("loading");
    apiClient
      .listPositions(canManage ? { status: statusFilter } : {})
      .then((response) => {
        if (cancelled) {
          return;
        }
        setPositions(response.positions);
        setNextCursor(response.next_cursor);
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey, statusFilter, canManage]);

  function refresh() {
    setReloadKey((key) => key + 1);
  }

  async function handleLoadMore() {
    if (nextCursor === undefined || loadingMore) {
      return;
    }
    const gen = listGen.current;
    setLoadingMore(true);
    try {
      const response = await apiClient.listPositions({
        ...(canManage ? { status: statusFilter } : {}),
        cursor: nextCursor
      });
      if (gen !== listGen.current) {
        // The filter changed while this page was in flight — discard it so we
        // don't append wrong-status rows or install a stale cursor.
        return;
      }
      setPositions((previous) => [...previous, ...response.positions]);
      setNextCursor(response.next_cursor);
    } catch {
      // Leave the button in place so the user can retry the next page.
    } finally {
      setLoadingMore(false);
    }
  }

  function openDetail(position: Position) {
    setActionError(null);
    setDetail(position);
  }

  function startEdit(position: Position) {
    setDetail(null);
    setFormPosition(position);
    setFormOpen(true);
  }

  function startCreate() {
    setFormPosition(null);
    setFormOpen(true);
  }

  function startRespond(position: Position) {
    setDetail(null);
    setResponding(position);
  }

  function requestDelete(position: Position) {
    setDetail(null);
    setPendingDelete(position);
    setDeleteError(null);
  }

  async function toggleStatus(position: Position) {
    const target = position.status === "open" ? "closed" : "open";
    setActionError(null);
    try {
      await apiClient.updatePosition(
        position.id,
        { status: target },
        // updated_at in the signature so a later re-close mints a new key
        // instead of replaying the first close's stored response.
        statusKey.keyFor(`status:${position.id}:${target}:${position.updated_at}`)
      );
      setDetail(null);
      refresh();
    } catch (caught) {
      setActionError(messageFor(caught, "Couldn't update position."));
    }
  }

  async function confirmDelete() {
    if (pendingDelete === null || deleting) {
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiClient.deletePosition(pendingDelete.id, deleteKey.keyFor(`delete:${pendingDelete.id}`));
      deleteKey.reset();
      setPendingDelete(null);
      refresh();
    } catch (caught) {
      setDeleteError(messageFor(caught, "Couldn't delete position."));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="positions-view">
      {canManage && (
        <section className="card positions-toolbar">
          <div className="positions-toolbar__filter">
            <label>
              Show
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
              >
                <option value="open">Open</option>
                <option value="closed">Closed</option>
                <option value="all">All</option>
              </select>
            </label>
          </div>
          <button type="button" className="btn btn--primary" onClick={startCreate}>
            New position
          </button>
        </section>
      )}

      {actionError !== null && <p className="form-row__error">{actionError}</p>}
      {status === "loading" && <p className="loading">Loading…</p>}
      {status === "error" && (
        <p className="form-row__error">
          Couldn't load positions.{" "}
          <button type="button" className="btn btn--ghost btn--sm" onClick={refresh}>
            Retry
          </button>
        </p>
      )}
      {status === "ready" && positions.length === 0 && (
        <div className="card empty-state">
          <h2>No open positions right now.</h2>
          {canManage && <p>Post one with “New position”.</p>}
        </div>
      )}
      {status === "ready" && positions.length > 0 && (
        <>
          <div className="block-card-grid positions-grid">
            {positions.map((position) => (
              <PositionCard
                key={position.id}
                position={position}
                canManage={canManage}
                onOpen={() => openDetail(position)}
                onRespond={() => startRespond(position)}
                onEdit={() => startEdit(position)}
                onToggleStatus={() => void toggleStatus(position)}
                onDelete={() => requestDelete(position)}
              />
            ))}
          </div>
          {nextCursor !== undefined && (
            <button
              type="button"
              className="btn btn--ghost btn--sm positions-view__more"
              onClick={() => void handleLoadMore()}
              disabled={loadingMore}
            >
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          )}
        </>
      )}

      {formOpen && (
        <PositionFormModal
          me={me}
          position={formPosition}
          onClose={() => setFormOpen(false)}
          onSaved={() => {
            setFormOpen(false);
            refresh();
          }}
        />
      )}
      {detail !== null && (
        <PositionDetail
          position={detail}
          canManage={canManage}
          error={actionError}
          onClose={() => setDetail(null)}
          onRespond={() => startRespond(detail)}
          onEdit={() => startEdit(detail)}
          onToggleStatus={() => void toggleStatus(detail)}
          onDelete={() => requestDelete(detail)}
        />
      )}
      {responding !== null && (
        <RespondModal position={responding} onClose={() => setResponding(null)} />
      )}
      {pendingDelete !== null && (
        <div className="modal-overlay" role="presentation" {...overlayDismiss}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-position-title"
          >
            <header className="modal__header">
              <h2 id="delete-position-title">Delete {pendingDelete.title}?</h2>
              <button
                type="button"
                className="modal__close"
                aria-label="Close"
                onClick={() => setPendingDelete(null)}
                disabled={deleting}
              >
                ×
              </button>
            </header>
            <div className="modal__body">
              <p>This permanently removes the posting and its videos. Closed positions can be kept instead — close rather than delete if you might reopen the role.</p>
              {deleteError !== null && <p className="form-row__error">{deleteError}</p>}
              <div className="modal__footer">
                <button type="button" className="btn btn--ghost" onClick={() => setPendingDelete(null)} disabled={deleting}>
                  Cancel
                </button>
                <button type="button" className="btn btn--danger" onClick={() => void confirmDelete()} disabled={deleting}>
                  {deleting ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PositionCard({
  position,
  canManage,
  onOpen,
  onRespond,
  onEdit,
  onToggleStatus,
  onDelete
}: {
  position: Position;
  canManage: boolean;
  onOpen: () => void;
  onRespond: () => void;
  onEdit: () => void;
  onToggleStatus: () => void;
  onDelete: () => void;
}) {
  const closed = position.status === "closed";
  const firstVideo = position.videos.length > 0 ? position.videos[0] : undefined;

  return (
    <article className="block-card position-card">
      <button type="button" className="position-card__open" onClick={onOpen}>
        <span className="block-card__name">{position.title}</span>
      </button>
      <div className="block-card__tags">
        <span className="chip">{position.department_name}</span>
        <span className="chip">
          {position.slots} opening{position.slots === 1 ? "" : "s"}
        </span>
        {position.employment_type !== undefined && (
          <span className="chip">{employmentTypeLabel(position.employment_type)}</span>
        )}
        {position.location !== undefined && <span className="chip">{position.location}</span>}
        {position.compensation_range !== undefined && (
          <span className="chip chip--impact">{position.compensation_range}</span>
        )}
        {closed && <span className="status-badge status-badge--closed">closed</span>}
      </div>
      {firstVideo !== undefined && (
        <VideoEmbed
          embedUrl={firstVideo.embed_url}
          title={firstVideo.label ?? `${position.title} video`}
        />
      )}
      <div className="position-card__actions">
        <button type="button" className="btn btn--primary btn--sm" onClick={onRespond} disabled={closed}>
          Respond
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onOpen}>
          Details
        </button>
        {canManage && (
          <>
            <button type="button" className="btn btn--ghost btn--sm" onClick={onEdit}>
              Edit
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={onToggleStatus}>
              {closed ? "Reopen" : "Close"}
            </button>
            <button type="button" className="btn btn--danger btn--sm" onClick={onDelete}>
              Delete
            </button>
          </>
        )}
      </div>
    </article>
  );
}

function messageFor(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) {
    if (caught.code === "not_found") {
      return "That position no longer exists.";
    }
    return caught.message;
  }
  return caught instanceof Error ? caught.message : fallback;
}
